import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { fetchInviteOverview, type InviteOverview } from '@/api/invite';
import { profileApi } from '@/api/auth';
import { getToken } from '@/lib/auth-storage';

export default function InvitePage() {
  const navigate = useNavigate();
  const [data, setData] = useState<InviteOverview | null>(null);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState<'code' | 'link' | null>(null);

  useEffect(() => {
    if (!getToken()) {
      navigate('/login?redirect=/invite');
      return;
    }
    setLoading(true);
    setLoadError('');
    fetchInviteOverview()
      .then((res) => {
        if (res.code === 0 && res.data) {
          setData(res.data);
          return;
        }
        return profileApi().then((pr) => {
          if (pr.code === 0 && pr.data?.referralCode) {
            const code = pr.data.referralCode;
            setData({
              referralCode: code,
              inviteLink: `${window.location.origin}/register?invite=${code}`,
              registerRewardSearchTimes: 3,
              searchPointsCost: 2,
              registerRewardPoints: 6,
              totalInvited: 0,
            });
          } else {
            setLoadError(res.msg || '加载失败，请稍后重试');
          }
        });
      })
      .catch(() => setLoadError('网络异常，请稍后重试'))
      .finally(() => setLoading(false));
  }, [navigate]);

  const copy = (type: 'code' | 'link') => {
    const text = type === 'code' ? data?.referralCode ?? '' : fullLink;
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const fullLink =
    data?.inviteLink?.startsWith('http')
      ? data.inviteLink
      : data?.inviteLink
        ? `${window.location.origin}${data.inviteLink.startsWith('/') ? '' : '/'}${data.inviteLink}`
        : data?.referralCode
          ? `${window.location.origin}/register?invite=${data.referralCode}`
          : '';

  const rewardSearchTimes = data?.registerRewardSearchTimes ?? 3;
  const rewardPoints = data?.registerRewardPoints ?? rewardSearchTimes * (data?.searchPointsCost ?? 2);

  return (
    <div className="min-h-screen flex flex-col bg-[#f7f7f8]">
      <Navbar />
      <main className="flex-1 max-w-2xl mx-auto w-full px-4 py-8">
        <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
          <Link to="/account" className="hover:text-gray-700">用户中心</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-gray-700 font-medium">邀请好友</span>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
          <h1 className="text-xl font-bold text-gray-900 mb-2">邀请好友赚积分</h1>
          {loading && <p className="text-sm text-gray-500 mb-4">加载中…</p>}
          {loadError && <p className="text-sm text-red-500 mb-4">{loadError}</p>}
          {!loading && !loadError && (
            <>
              <p className="text-sm text-gray-500 mb-6">
                好友通过您的邀请码注册成功后，您将获得{' '}
                可用于 <span className="font-semibold text-gray-800">{rewardSearchTimes}</span> 次首页机器人搜索的积分奖励
                （当前为 <span className="font-semibold text-gray-800">{rewardPoints}</span> 积分）。
              </p>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">我的邀请码</label>
                  <div className="mt-1 flex gap-2">
                    <code className="flex-1 bg-gray-50 border rounded-lg px-3 py-2 text-lg tracking-widest">
                      {data?.referralCode || '—'}
                    </code>
                    <button
                      type="button"
                      disabled={!data?.referralCode}
                      onClick={() => copy('code')}
                      className="px-4 py-2 bg-gray-900 text-white text-sm rounded-lg disabled:opacity-50"
                    >
                      {copied === 'code' ? '已复制' : '复制'}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">邀请链接</label>
                  <div className="mt-1 flex gap-2">
                    <input
                      readOnly
                      value={fullLink}
                      className="flex-1 bg-gray-50 border rounded-lg px-3 py-2 text-sm text-gray-600"
                    />
                    <button
                      type="button"
                      disabled={!fullLink}
                      onClick={() => copy('link')}
                      className="px-4 py-2 border border-gray-200 text-sm rounded-lg disabled:opacity-50"
                    >
                      {copied === 'link' ? '已复制' : '复制链接'}
                    </button>
                  </div>
                </div>

                <div className="pt-4 border-t text-sm text-gray-600">
                  已成功邀请 <span className="font-bold text-gray-900">{data?.totalInvited ?? 0}</span> 人
                </div>
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
